import classNames from "classnames";

const GROUP_CLASSES = {
  tax: { header: "group-tax", cell: "group-tax" },
  real: { header: "group-real", cell: "group-real" },
};

const TickerCell = ({ value, lot}) => {
  return(
    <>
      <span className="c-tax-harvesting-table__ticker">{value}</span>
      <span className="c-tax-harvesting-table__name">{lot.calculated.Security}</span>
    </>
  )
} ;

const TypeCell = ({ value }) => (
  <>
    <span className={classNames("c-tax-harvesting-table__dot", value === 'SIC' ? 'is-blue' : value === 'NO SIC' ? 'is-red' : 'is-orange')} />
    <span className="c-tax-harvesting-table__type">{value}</span>
  </>
);

const parseMxn = (value) => {
  const number = String(value || "").replace(/[^0-9.-]/g, "");
  return parseFloat(number) || 0;
};

const sumBy = (rows, key) =>
  rows.reduce((acc, row) => acc + parseMxn(row[key]), 0);

const formatMxn = (value) =>
  `MX$${Math.round(Math.abs(value)).toLocaleString("en-US")}`;

const renderCell = (key, value, lot) => {
  if (key === "Ticker" || key === "Security") {
    return <TickerCell value={value} lot={lot} />;
  }
  if (key === "Type") {
    return <TypeCell value={value} />;
  }
  return value;
};

const ABBREVIATIONS = {
  adj: "adjusted",
  qty: "quantity",
  amt: "amount",
  bal: "balance",
};

const FILLER_WORDS = [
  "include",
  "includes",
  "including",
  "amortization",
  "accretion",
  "effect",
  "effects",
  "and",
  "of",
  "the",
];

const normalizeTokens = (key) =>
  String(key)
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
    .filter((token) => !FILLER_WORDS.includes(token))
    .map((token) => {
      let normalized = ABBREVIATIONS[token] || token;
      if (normalized.length > 3 && normalized.endsWith("s") && !normalized.endsWith("ss")) {
        normalized = normalized.slice(0, -1);
      }
      return normalized;
    });

const getTotalsForTab = (totals, activeTab) => {
  if (!totals) return null;
  const scoped = totals[activeTab];
  return scoped && typeof scoped === "object" ? scoped : totals;
};

const buildTotalsIndex = (totals) =>
  Object.entries(totals).map(([key, value]) => ({
    tokens: normalizeTokens(key),
    tokenKey: normalizeTokens(key).join(""),
    value,
  }));

const isSubset = (a, b) => [...a].every((token) => b.has(token));

const findTotalValue = (index, key) => {
  const target = normalizeTokens(key);
  const targetKey = target.join("");
  if (!targetKey) return undefined;

  const exact = index.find((entry) => entry.tokenKey === targetKey);
  if (exact) return exact.value;

  const targetSet = new Set(target);
  const loose = index.filter(
    (entry) =>
      entry.tokens.length > 0 &&
      (isSubset(targetSet, new Set(entry.tokens)) ||
        isSubset(new Set(entry.tokens), targetSet))
  );
  return loose.length === 1 ? loose[0].value : undefined;
};

const isLeftColumn = (key) => ["Ticker", "Type", "Security"].includes(key);

const getRowTicker = (row, lot) =>
  row.Ticker ||
  (lot &&
    lot.display &&
    lot.display.calculated &&
    lot.display.calculated.Ticker) ||
  (lot && lot.calculated && lot.calculated.Ticker) ||
  (lot && lot.ticker_symbol);

const PositionsTable = ({ totals, openLots, closeLots, activeTab, tableData, selectedTickers }) => {
  const allRows = (tableData && tableData[activeTab]) || [];
  const lots = (activeTab.includes("open") ? openLots : closeLots) || [];
  const allPairs = allRows.map((row, index) => ({ row, lot: lots[index] }));
  const pairs = Array.isArray(selectedTickers)
    ? allPairs.filter(({ row, lot }) => {
        const ticker = getRowTicker(row, lot);
        return !ticker || selectedTickers.includes(ticker);
      })
    : allPairs;
  const columns = allRows.length > 0 ? Object.keys(allRows[0]) : [];
  const tabTotals = getTotalsForTab(totals, activeTab);
  const totalsIndex = buildTotalsIndex(tabTotals || {});
  return (
    <div className="c-tax-harvesting-table__wrapper">
      <table className="c-tax-harvesting-table">
        <thead>
          <tr className="c-tax-harvesting-table__head-row">
            {columns.map((label) => {
              return (
                <th
                  key={label}
                  className={classNames(
                    "c-tax-harvesting-table__head-cell",
                    isLeftColumn(label) && "is-left",
                    ["Tax MXN Loss Harvesting", "Tax Due"].includes(label) && "is-blue",
                    ["Real MXN P/L", "Real Rate of Return", "UDI Adj. MXN Cost Basis", "Actual Value MXN"].includes(label) && "is-yellow",
                  )}
                >
                  {label}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {pairs.map(({ row, lot }, index) => {
            return (
              <tr key={`table-row-${index}`} className="c-tax-harvesting-table__row">
                {columns.map((key, idx) => {
                  const value = row[key];
                  return (
                    <td
                      key={`${key}-${idx}`}
                      className={classNames(
                        "c-tax-harvesting-table__cell",
                        isLeftColumn(key) && "is-left",
                        ["Ticker", "Type"].includes(key) && "is-bold",
                        ["Tax MXN Loss Harvesting", "Tax Due"].includes(key) && "is-blue",
                        ["Real MXN P/L", "Real Rate of Return", "UDI Adj. MXN Cost Basis", "Actual Value MXN"].includes(key) && "is-yellow",
                        value && value.startsWith('-') && "is-negative",
                      )}
                    >
                      {lot && renderCell(key, value, lot)}
                    </td>
                  )
                }
                )}
              </tr>
            )
          })}
          {tabTotals && columns.length > 0 && (
            <tr className="c-tax-harvesting-table__row c-tax-harvesting-table__row--totals">
              {columns.map((key, index) => {
                const value = findTotalValue(totalsIndex, key);
                let content = value === undefined || value === null ? "–" : value;
                if (index === 0) {
                  content = "Total";
                }
                if (key === "Type") {
                  content = "";
                }
                return (
                  <td
                    key={`totals-${key}`}
                    className={classNames(
                      "c-tax-harvesting-table__cell",
                      "c-tax-harvesting-table__cell--totals",
                      isLeftColumn(key) && "is-left"
                    )}
                  >
                    {content}
                  </td>
                );
              })}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

}
export default PositionsTable;
export { sumBy, formatMxn };
